// Third Party
import * as React from 'react';
import { v4 } from 'uuid';

// UI
import {
  NotificationOverlay,
  NotificationPayload,
} from '../components/notification';

type NotificationCreatePayload = Omit<NotificationPayload, 'uniqueId'>;

export type NotificationContextValue = {
  addNotification: (
    notification: NotificationCreatePayload,
    uniqueId?: string
  ) => void;
  clearNotification: () => void;
  clearNotifications: () => void;
  removeNotification: (notificationId: string) => void;
  setNotification: (notification: NotificationPayload) => void;
  showUnauthenticatedError: (options?: { consoleMessage?: string }) => void;
  showGenericError: (options?: {
    consoleMessage?: string;
    timeout: number;
  }) => void;
};

// # region [rgba(0, 75, 75, 0.1)] --- Provider

interface Props {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<Props> = ({ children }) => {
  const [notifications, setNotifications] = React.useState<
    NotificationPayload[]
  >([]);

  // Add a single notification
  const addNotification = (
    notification: NotificationCreatePayload,
    uniqueId = v4()
  ) => {
    const notificationToAdd = { ...notification, ...{ uniqueId: uniqueId } };
    // Functional state updates have a lint bug when the state's type is imported
    setNotifications((prevNotifications) => [
      ...prevNotifications,
      notificationToAdd,
    ]);
  };

  // A flag that triggers all notifications to begin their removal process
  const [markAllForRemoval, setMarkAllForRemoval] = React.useState(false);

  // Remove all notifications
  const clearNotifications = () => {
    if (notifications.length) setMarkAllForRemoval(true);
  };

  // TODO This was kept for backwards compatability / to reduce ticket surface area
  // Will need to sweep through codebase and pluralize the call before removing.
  const clearNotification = () => clearNotifications();

  // Remove a single notification
  const removeNotification = (notificationId: string) => {
    setNotifications((prevNotifications) => {
      // Functional state updates have a lint bug when the state's type is imported
      return prevNotifications.filter(
        (notification) => notification.uniqueId !== notificationId
      );
    });
  };

  // Disable the markAllForRemovalFlag once all notifications have been removed
  React.useEffect(() => {
    if (!notifications.length && markAllForRemoval) setMarkAllForRemoval(false);
  }, [notifications.length, markAllForRemoval]);

  const contextValue: NotificationContextValue = {
    addNotification,
    clearNotification,
    clearNotifications,
    removeNotification,
    setNotification: (notification) => addNotification(notification),
    showUnauthenticatedError: () => {
      addNotification({
        title: 'User is unauthenticated',
        message:
          'An authentication error occurred. Please sign in and try again.',
        type: 'error',
        timeout: 5000,
      });
    },
    showGenericError: (options?: {
      consoleMessage?: string;
      timeout: number;
    }) => {
      addNotification({
        message:
          'An unexpected error occurred. Please refresh or try again later.',
        title: 'An Error Occurred',
        timeout: options?.timeout,
        type: 'error',
      });
    },
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      <NotificationOverlay
        markAllForRemoval={markAllForRemoval}
        notifications={notifications}
        removeNotification={removeNotification}
      />
      {children}
    </NotificationContext.Provider>
  );
};

// #endregion

export const NotificationContext = React.createContext<
  NotificationContextValue | undefined
>(undefined);

export const useNotification = () => {
  const context = React.useContext(NotificationContext);

  if (context === undefined) {
    throw new Error(
      'useNotification must be used within a NotificationProvider'
    );
  }

  return context;
};
